import { inject } from '@angular/core';
import { PostItem } from '@app/core/models';
import { Store } from '@ngrx/store';
import { ItemFn } from '../item-post/item-post.component';

import { Dialog } from '@angular/cdk/dialog';
import { ACADEMIC_YEAR_KEY } from '@app/config';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { ActivityService, AlertService, NotificationService } from '@app/core/services';
import { helperTarget, ModalDailyTab } from '@app/modules/eclass/components/modal-daily/base';
import { ModalDailyComponent } from '@app/modules/eclass/components/modal-daily/modal-daily.component';
import { Checker, helperAcademicYear, notNull, safeDate, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { first } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { FilterService } from '../../services/filter.service';
import { Header } from '../../utils/postable';
import { ModalDuplicateActivityComponent } from '../modal-duplicate-activity/modal-duplicate-activity.component';
import { ModalReviewComponent } from '../modal-review/modal-review.component';
import { ModalViewDailyComponent } from '../modal-view-daily/modal-view-daily.component';
import { FilterControlEnum, FilterForm, StatusFilter, StatusFilterTypeEnum } from '../posts-filter-bar/filter-bar';
import { ExtraActivityComponent } from './extra-activity.component';
import { SubtitleActivityComponent } from './subtitle-activity.component';

interface Service {
  store: Store;
  dialog: Dialog;
  alert: AlertService;
  filterService: FilterService;
  activityService: ActivityService;
  notificationService: NotificationService;
}

export const ActivityFilter = (state: AppState) => ({
  status: status(state),
  filters: filters(state),
});

export const ActivityHeader: Header = {
  plural: 'eclass.agenda.filtros.atividade.plural',
  singular: 'eclass.agenda.filtros.atividade.singular',
  icon: 'icon-class',
  worker: EclassWorkerEnum.Activity,
};

export const ActivityItem: ItemFn = () => {
  const services: Service = {
    store: inject(Store),
    dialog: inject(Dialog),
    alert: inject(AlertService),
    filterService: inject(FilterService),
    activityService: inject(ActivityService),
    notificationService: inject(NotificationService),
  };

  return {
    ...ActivityHeader,
    color: 'border-mantis-500 outline-mantis-500',
    iconStyle: 'text-mantis-700 bg-mantis-500/10',
    labelStyle: 'border-mantis-500 text-mantis-700',
    subtitle: SubtitleActivityComponent,
    extra: ExtraActivityComponent,
    action: (item) => readOnly(item, services),
    options: (item) => options(item, services),
  };
};

function canEdit(item: PostItem, service: Service) {
  return combineLatest([
    service.store.select(AppSelectors.isAllow([20, 36])),
    service.store.select(AppSelectors.ActiveUser),
  ]).pipe(
    take(1),
    map(([allow, activeUser]) => ({
      allow,
      currentYear: helperAcademicYear(item.date, activeUser) === activeUser?.ano_letivo,
    })),
  );
}

function options(item: PostItem, service: Service) {
  return canEdit(item, service).pipe(
    map(({ allow, currentYear }) => {
      if (allow) {
        if (currentYear) {
          return [
            {
              icon: 'icon-edit',
              title: 'geral.editar',
              action: () => edit(item, service),
            },
            {
              icon: 'icon-book-pencil',
              title: 'eclass.tarefa.ver-respostas',
              action: () => correction(item, service, currentYear),
            },
            {
              icon: 'icon-duplicate-2',
              title: 'geral.duplicar',
              action: () => duplicate(item, service),
            },
            {
              icon: 'icon-trash-2',
              title: 'geral.remover',
              action: () => remove(item, service),
            },
          ];
        }
        return [
          {
            icon: 'icon-book-pencil',
            title: 'geral.ver-resposta',
            action: () => correction(item, service, currentYear),
          },
          {
            icon: 'icon-duplicate-2',
            title: 'geral.duplicar',
            action: () => duplicate(item, service),
          },
        ];
      }
      return [];
    }),
  );
}

function correction(item: PostItem, service: Service, currentYear = false) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.activityService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    map((activity) => ({
      activity,
    })),
    switchMap((data) => service.dialog.open(ModalReviewComponent, { data, disableClose: true }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function readOnly(item: PostItem, service: Service, currentYear = false) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.activityService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    map((activity) => ({
      worker: EclassWorkerEnum.Activity,
      readOnly: !currentYear,
      element: activity,
      subtitle: SubtitleActivityComponent,
      item,
    })),
    switchMap((data) => service.dialog.open(ModalViewDailyComponent, { data, disableClose: true }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function duplicate(item: PostItem, service: Service) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);

  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.activityService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    switchMap((data) => service.dialog.open(ModalDuplicateActivityComponent, { data }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function edit(item: PostItem, service: Service) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.activityService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    map((activity) => ({
      current: ModalDailyTab.Activity,
      date: safeDate(item.date),
      entityId: Number(item.entity_id),
      groupId: first(item.targets)?.group_id,
      subjectId: first(item.targets)?.subject_id,
      data: { target: { ...helperTarget(activity.daily), id: activity?.id }, data: activity },
      learningTrailsElements: activity?.trail_elements,
      games: activity?.games,
    })),
    switchMap((data) => service.dialog.open(ModalDailyComponent, { data, disableClose: true }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function remove(item: PostItem, service: Service) {
  return service.alert.confirm({ message: 'eclass.aula.confirmar-remover' }).pipe(
    notNull(),
    switchMap(() => service.activityService.destroy(item.reference_id).pipe(catchError(() => of({ ret: 0 })))),
    tap((res) => {
      if (res.ret === 1) {
        service.notificationService.success('geral.sucesso');
        service.filterService.refresh();
      } else {
        service.notificationService.error('eclass.aula.erro-ao-excluir');
      }
    }),
  );
}

function status(store: AppState): StatusFilter[] {
  const status: StatusFilter[] = [
    {
      key: StatusFilterTypeEnum.IsConcluded,
      type: StatusFilterTypeEnum.IsConcluded,
      value: undefined,
      description: 'geral.todos',
    },
  ];
  if (Checker.isStudentOrResponsible(store.funcao_padrao)) {
    status.push(
      {
        key: `${StatusFilterTypeEnum.IsConcluded}#1`,
        type: StatusFilterTypeEnum.IsConcluded,
        value: 1,
        description: 'eclass.relatorios.concluidos',
      },
      {
        key: `${StatusFilterTypeEnum.IsConcluded}#0`,
        type: StatusFilterTypeEnum.IsConcluded,
        value: 0,
        description: 'eclass.relatorios.pendentes',
      },
    );
  } else {
    status.push({
      key: `${StatusFilterTypeEnum.HasAnswers}#1`,
      type: StatusFilterTypeEnum.HasAnswers,
      value: 1,
      description: 'eclass.relatorios.respondidos',
    });
  }
  return status;
}

function filters(store: AppState): FilterForm {
  const filters = [FilterControlEnum.Group, FilterControlEnum.Subject, FilterControlEnum.Generic];
  if (Checker.isEmployee(store.funcao_padrao)) {
    filters.push(FilterControlEnum.Level, FilterControlEnum.Grade);
    if (!Checker.isTeacher(store.funcao_padrao)) {
      filters.push(FilterControlEnum.Author);
    }
  }
  return {
    fixed: [FilterControlEnum.Group, FilterControlEnum.Subject],
    filters,
  };
}
